import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faRocket, 
  faUsers, 
  faAward, 
  faGlobe,
  faLightbulb,
  faHandshake,
  faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import CompanyInfo from '../components/CompanyInfo';
import MapComponent from '../components/MapComponent';

gsap.registerPlugin(ScrollTrigger);

const About = () => {
  const pageRef = useRef(null);


  useEffect(() => {
    // 页面入场动画
    gsap.fromTo(
      pageRef.current.children,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.2,
        ease: 'power2.out'
      }
    );
  }, []);



  const values = [
    {
      icon: faLightbulb,
      title: '创新驱动',
      description: '持续技术创新，为客户提供最前沿的财务解决方案'
    },
    {
      icon: faHandshake,
      title: '客户至上',
      description: '以客户需求为导向，提供专业贴心的服务体验'
    },
    {
      icon: faShieldAlt,
      title: '安全可靠',
      description: '严格的安全标准，确保客户数据和资金安全'
    },
    {
      icon: faAward,
      title: '专业品质',
      description: '专业的团队，严谨的态度，打造高品质产品'
    }
  ];

  const milestones = [
    {
      year: '2020',
      title: '公司成立',
      description: '云帐天下正式成立，专注物流财务数字化'
    },
    {
      year: '2021',
      title: '产品发布',
      description: '首款智能财务系统正式上线，服务首批客户'
    },
    {
      year: '2022',
      title: '快速发展',
      description: '客户数量突破1000家，获得行业认可'
    },
    {
      year: '2023',
      title: '技术升级',
      description: 'AI技术全面升级，智能化水平大幅提升'
    },
    {
      year: '2024',
      title: '行业领先',
      description: '成为物流财务数字化领域的领军企业'
    }
  ];





  return (
    <div ref={pageRef} className="page-transition">
      {/* 英雄区域 */}
      <section className="py-20 bg-gradient-hero text-white flex items-center min-h-[300px]">
        <div className="container mx-auto px-4 text-center">
          <p className="text-2xl md:text-3xl font-medium text-blue-100">
            专注于物流快运行业的智能财务解决方案提供商
          </p>
        </div>
      </section>

      {/* 公司介绍 */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold gradient-text mb-6">
                我们的使命
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                云帐天下致力于通过先进的技术和专业的服务，为物流快运企业提供智能化的财务管理解决方案，
                帮助企业提升财务管理效率，降低运营成本，实现数字化转型。
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                我们深耕物流行业多年，深刻理解行业痛点和需求，结合最新的人工智能、大数据等技术，
                为客户打造专业、安全、高效的财务管理平台。
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary-blue mb-2">13,800+</div>
                  <div className="text-gray-600">服务企业</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary-blue mb-2">99.9%</div>
                  <div className="text-gray-600">系统稳定性</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="/task.jpg"
                alt="团队合作"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-primary-cyan rounded-full flex items-center justify-center">
                <FontAwesomeIcon icon={faRocket} className="text-white text-2xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 核心价值观 */}
      <section className="py-20 bg-background-light">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold gradient-text mb-6">
              核心价值观
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              我们的价值观指引着我们的每一个决策和行动
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <div className="w-16 h-16 bg-gradient-primary rounded-full flex items-center justify-center mx-auto mb-6">
                  <FontAwesomeIcon icon={value.icon} className="text-white text-2xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 发展历程 */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold gradient-text mb-6">
              发展历程
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              从初创到行业领先，见证我们的成长足迹
            </p>
          </div>
          
          <div className="relative">
            {/* 时间线 */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-primary rounded-full"></div>
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8'}`}>
                    <div className="bg-white rounded-2xl p-6 shadow-lg">
                      <div className="text-2xl font-bold text-primary-blue mb-2">{milestone.year}</div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{milestone.title}</h3>
                      <p className="text-gray-600">{milestone.description}</p>
                    </div>
                  </div>
                  
                  <div className="relative z-10">
                    <div className="w-4 h-4 bg-primary-cyan rounded-full border-4 border-white shadow-lg"></div>
                  </div>
                  
                  <div className="w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>



      {/* 联系方式 */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <CompanyInfo 
              title="联系我们" 
              description="多种联系方式，专业团队为您提供一对一服务" 
            />
          </div>
        </div>
      </section>

      {/* 地图区域 */}
      <section className="py-20 bg-background-light">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold gradient-text mb-6">
              公司位置
            </h2>
            <p className="text-xl text-gray-600">
              欢迎您到访我们的办公室，我们期待与您面对面交流
            </p>
          </div>
          
          <MapComponent />
        </div>
      </section>
    </div>
  );
};

export default About;